import type { ComponentProps } from "react";
import {
  Link as RouterLink,
  useLocation,
  type To,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_LOCALE,
  isSupportedLocale,
  splitLocalePrefix,
} from "./locales";

/**
 * Keep internal navigation inside the active locale namespace. External,
 * hash, and relative links are deliberately returned untouched. Chart
 * routes still carry their ordinary path and only the chrome URL prefix is
 * changed; no chart geometry is involved.
 */
export function localizeTo(to: To, pathname: string, language: string): To {
  if (typeof to === "object" && to !== null && "pathname" in to) {
    return {
      ...to,
      pathname: localizeTo(to.pathname ?? "/", pathname, language) as string,
    };
  }
  if (typeof to !== "string" || !to.startsWith("/") || to.startsWith("//")) {
    return to;
  }

  const explicit = splitLocalePrefix(to);
  if (explicit.locale) return to;

  const activeFromPath = splitLocalePrefix(pathname).locale;
  const active = activeFromPath ?? (isSupportedLocale(language) ? language : DEFAULT_LOCALE);
  if (active === DEFAULT_LOCALE) return to;
  return `/${active}${to === "/" ? "" : to}`;
}

type LocaleLinkProps = ComponentProps<typeof RouterLink>;

export function LocaleLink({ to, ...props }: LocaleLinkProps) {
  const location = useLocation();
  const { i18n } = useTranslation();
  return <RouterLink to={localizeTo(to, location.pathname, i18n.language)} {...props} />;
}

export default LocaleLink;
